import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import s from './EntitySubType.module.scss';
import { useSelector, useDispatch } from 'react-redux';
import { getEntitySubType, getGeneralData } from '../../../selectors/index';
import { FormattedMessage, injectIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { initialEntitySubTypeState } from '../../../store/reducers/initialState';
import uuidv4 from 'uuid/v4';
import { useParams, Redirect, useHistory } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import GoBack from '../../WSAControls/GoBack/GoBack';
import classNames from 'classnames';
import { isRequired, compareValues } from '../../../utils';
import Cookies from 'js-cookie';
import OperationalSetupContentTemplate from '../../OperationalSetup/OperationalSetupContentTemplate';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import Dropdown from '../../WSAControls/DroprdownContainer/Dropdown';
import Banner from '../../Banner/Banner';
import {
  describeEntitySubTypeThunk,
  saveEntitySubTypeThunk,
  setEntitySubTypeChangeThunk,
  setEntitySubTypeCleanUpThunk
} from '../../../store/actions/admin/entitySubType';
import uuid from 'uuid';

const envName = process.env.REACT_APP_ENV_NAME_SHORT;

const EntitySubType = ({ intl }) => {
  let history = useHistory();
  let { entitySubTypeId } = useParams();

  const dispatch = useDispatch();
  const entitySubType = useSelector(state => getEntitySubType(state));
  const userProfileWidgets = useSelector(state => state.userProfileWidgets);
  const generalData = useSelector(state => getGeneralData(state));

  let IsEdit = false;

  const [localformErrors, setFormErrors] = useState({});

  const backUrl = '/operational-setup/entity-sub-types';
  let widgetCode = '';
  if (entitySubTypeId) {
    IsEdit = true;
    widgetCode = 'WQSP_ENTITY_SUB_TYPES_EDIT';
  }

  useEffect(() => {
    if (entitySubTypeId) {
      dispatch(describeEntitySubTypeThunk(entitySubTypeId, widgetCode));
    }
  }, [describeEntitySubTypeThunk, dispatch, entitySubTypeId]);

  //clean up
  useEffect(() => {
    return () => {
      dispatch(setEntitySubTypeCleanUpThunk(initialEntitySubTypeState.entitySubType.SelectedEntitySubType));
    };
  }, []);

  // Update redux store
  const setLocalEntitySubType = currentState => {
    dispatch(setEntitySubTypeChangeThunk(currentState));
  };

  const entityTypeOptions = () => {
    let opData = [];

    const data = generalData.entityTypes;

    data.forEach(element => {
      opData.push({
        label: element.Name,
        value: element.EntityTypeId
      });
    });
    return opData.sort(compareValues('label'));
  };

  //Saving
  const submitForm = e => {
    e.preventDefault();

    if (!validateInput()) {
      return;
    }

    let saveData = entitySubType.SelectedEntitySubType;
    saveData.PerformDelete = false;
    saveData.IsEdit = IsEdit;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;

    if (!IsEdit) {
      saveData.EntitySubTypeId = 0;
      saveData.GuidIdentifier = uuidv4();
      widgetCode = 'WQSP_ENTITY_SUB_TYPES_ADDNEW';
    }

    dispatch(saveEntitySubTypeThunk(saveData, widgetCode));
  };

  const validateInput = () => {
    let formErrors = {};
    let isValid = true;

    if (
      !entitySubType ||
      !entitySubType.SelectedEntitySubType ||
      !entitySubType.SelectedEntitySubType.EntityTypeId ||
      isRequired(entitySubType.SelectedEntitySubType.EntityTypeId, 1)
    ) {
      formErrors.EntityTypeId = intl.formatMessage({
        id: 'wqsp.common.entityTypeMandatory',
        defaultMessage: 'Entity Type is a mandatory field'
      });
      isValid = false;
    }

    if (
      !entitySubType ||
      !entitySubType.SelectedEntitySubType ||
      !entitySubType.SelectedEntitySubType.Name ||
      isRequired(entitySubType.SelectedEntitySubType.Name, 1)
    ) {
      formErrors.Name = intl.formatMessage({
        id: 'common.nameRequired',
        defaultMessage: 'Name is a mandatory field'
      });
      isValid = false;
    }

    setFormErrors(formErrors);

    return isValid;
  };

  const onDropdownChange = (e, fieldName) => {
    setLocalEntitySubType({
      ...entitySubType,
      SelectedEntitySubType: { ...entitySubType.SelectedEntitySubType, [fieldName]: e.value }
    });
  };

  //on control value change
  const onChange = e => {
    setLocalEntitySubType({
      ...entitySubType,
      SelectedEntitySubType: { ...entitySubType.SelectedEntitySubType, [e.target.name]: e.target.value }
    });
  };

  const cancelHandler = () => {
    history.push(backUrl);
  };

  let messageId = (entitySubType && entitySubType.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const title = (entitySubType.SelectedEntitySubType ? entitySubType.SelectedEntitySubType.Name : '') || '';
  let heading = '';
  if (IsEdit) {
    heading = intl.formatMessage({ id: 'common.edit', defaultMessage: 'Edit' }) + ' ' + title;
  } else {
    heading = intl.formatMessage({ id: 'entitySubTypes.addEntitySubType', defaultMessage: 'Add new Entity Sub Type' });
  }

  let ddlClassSensor = localformErrors && localformErrors.FieldType ? s.ddlError : '';

  return (
    <div className={s.entitySubType}>
      {entitySubType.isLoading && <LoadingSpinner />}
      {entitySubType.isRedirect ? <Redirect to={backUrl} /> : ''}
      <Banner
        key={uuid()}
        failureText={messageText}
        showBanner={entitySubType.showBanner}
        status={entitySubType.isOpSuccessful}
        successText={messageText}
      />
      <OperationalSetupContentTemplate selectedPage="entitySubTypes" userProfileWidgets={userProfileWidgets}>
        <div className={s.contentWrapper}>
          <div className={s.entitySubTypeHeader}>
            <GoBack className={s.backLink}>
              &lt; &nbsp;
              <FormattedMessage id="common.back" defaultMessage="BACK" />
            </GoBack>
            <h3>{heading}</h3>
          </div>
          <div className={s.entitySubTypeContent}>
            <Form>
              <div className={s.topRow}>
                <Row>
                  <Col lg={3}>
                    <Form.Group controlId="entityType">
                      <Form.Label>
                        <FormattedMessage id="wqsp.common.entityType" defaultMessage="Entity Type" />
                      </Form.Label>

                      <Dropdown
                        id="formEntityType"
                        dataArray={entityTypeOptions()}
                        controlData={{
                          placeholderText: <FormattedMessage id="common.selectOne" defaultMessage="Please select one" />,
                          customClassName: ddlClassSensor
                        }}
                        name="EntityTypeId"
                        onDropdownChange={e => onDropdownChange(e, 'EntityTypeId')}
                        selectedOption={entityTypeOptions().filter(
                          option => option.value === entitySubType.SelectedEntitySubType.EntityTypeId
                        )}
                        disabled={IsEdit}
                      />
                      {localformErrors && localformErrors.EntityTypeId && (
                        <p role="alert" className={s.error}>
                          {localformErrors.EntityTypeId}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col lg={3}>
                    <Form.Group controlId="formEntitySubTypeName">
                      <Form.Label>
                        <FormattedMessage id="common.name" defaultMessage="Name" />
                      </Form.Label>

                      <Form.Control
                        type="text"
                        name="Name"
                        onChange={onChange}
                        value={entitySubType.SelectedEntitySubType.Name}
                        className={`${s.formControl} ${localformErrors && localformErrors.Name ? s.formControlError : ''}`}
                        placeholder={intl.formatMessage({
                          id: 'common.name',
                          defaultMessage: 'Name'
                        })}
                      />
                      {localformErrors && localformErrors.Name && (
                        <p role="alert" className={s.error}>
                          {localformErrors.Name}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group controlId="formEntitySubTypeDescription">
                      <Form.Label>
                        <FormattedMessage id="common.description" defaultMessage="Description" />
                      </Form.Label>

                      <Form.Control
                        className={s.textArea}
                        as="textarea"
                        rows="3"
                        name="Description"
                        onChange={onChange}
                        placeholder={intl.formatMessage({
                          id: 'common.description',
                          defaultMessage: 'Description'
                        })}
                        value={entitySubType.SelectedEntitySubType.Description}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button
                      variant="primary"
                      className={classNames(s.margin5, s.btnSaveChanges)}
                      onClick={submitForm}
                      noValidate
                      data-unittest="saveData"
                    >
                      <FormattedMessage id="common.save" defaultMessage="SAVE" />
                    </Button>

                    <Button
                      variant="outline-secondary"
                      className={classNames(s.btnCancel)}
                      onClick={cancelHandler}
                      noValidate
                      data-unittest="saveCancel"
                    >
                      <FormattedMessage id="common.cancel" defaultMessage="CANCEL" />
                    </Button>
                  </Col>
                </Row>
              </div>
            </Form>
          </div>
        </div>
      </OperationalSetupContentTemplate>
    </div>
  );
};

EntitySubType.defaultProps = {
  entitySubType: {
    ...initialEntitySubTypeState
  }
};

export default injectIntl(EntitySubType);
